import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  Zap,
  Phone,
  Mail,
  Lock,
  Loader2,
  Inbox,
  RefreshCw,
  LogOut,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY_STORE = "es_admin_key";

const Admin = () => {
  const [key, setKey] = useState(() => sessionStorage.getItem(KEY_STORE) || "");
  const [draft, setDraft] = useState("");
  const [quotes, setQuotes] = useState([]);
  const [state, setState] = useState("idle"); // idle | loading | ready | error
  const [lastFetch, setLastFetch] = useState(null);

  const load = useCallback(async (k, silent = false) => {
    if (!silent) setState("loading");
    try {
      const res = await axios.get(`${API}/quotes`, {
        headers: { "X-Admin-Key": k },
      });
      setQuotes(res.data);
      setState("ready");
      setLastFetch(new Date());
      return true;
    } catch {
      setState("error");
      return false;
    }
  }, []);

  useEffect(() => {
    if (!key) return;
    let active = true;
    const guarded = async (silent) => {
      const ok = await load(key, silent);
      if (!ok && active) {
        sessionStorage.removeItem(KEY_STORE);
        setKey("");
      }
    };
    guarded(false);
    const t = setInterval(() => guarded(true), 5000);
    return () => {
      active = false;
      clearInterval(t);
    };
  }, [key, load]);

  const unlock = async (e) => {
    e.preventDefault();
    const ok = await load(draft.trim());
    if (ok) {
      sessionStorage.setItem(KEY_STORE, draft.trim());
      setKey(draft.trim());
    }
  };

  if (!key) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-5">
        <form
          data-testid="admin-login-form"
          onSubmit={unlock}
          className="w-full max-w-sm bg-slate-900 border border-slate-800 rounded-md p-8"
        >
          <div className="flex items-center gap-2.5 mb-8">
            <span className="w-9 h-9 bg-blue-600 rounded-md flex items-center justify-center">
              <Zap className="w-5 h-5 text-amber-400" fill="currentColor" />
            </span>
            <span className="font-heading text-white font-bold">Leads Inbox</span>
          </div>
          <label htmlFor="admin-key" className="block text-sm font-bold text-slate-300 mb-2">
            Passcode
          </label>
          <input
            id="admin-key"
            data-testid="admin-passcode-input"
            type="password"
            required
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Enter passcode"
            className="w-full h-12 bg-slate-950 border border-slate-700 rounded-md px-4 text-white placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-amber-400"
          />
          {state === "error" && (
            <p data-testid="admin-login-error" className="mt-3 text-sm font-semibold text-red-400">
              Wrong passcode — try again.
            </p>
          )}
          <button
            data-testid="admin-login-button"
            type="submit"
            disabled={state === "loading"}
            className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-amber-400 hover:bg-amber-300 disabled:opacity-60 text-slate-950 font-bold py-3.5 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-amber-400"
          >
            {state === "loading" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
            Open inbox
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200">
      <header className="border-b border-slate-800">
        <div className="max-w-5xl mx-auto px-5 py-5 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2.5">
            <span className="w-9 h-9 bg-blue-600 rounded-md flex items-center justify-center">
              <Zap className="w-5 h-5 text-amber-400" fill="currentColor" />
            </span>
            <div>
              <p className="font-heading font-bold text-white leading-none">Leads Inbox</p>
              <p className="text-xs text-slate-500 mt-1">Essex Spark Electrical</p>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-4">
            <span
              data-testid="admin-live-indicator"
              className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-green-400"
            >
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              Live
            </span>
            <span data-testid="admin-count" className="text-sm font-bold text-white">
              {quotes.length} {quotes.length === 1 ? "enquiry" : "enquiries"}
            </span>
            <button
              data-testid="admin-logout-button"
              onClick={() => {
                sessionStorage.removeItem(KEY_STORE);
                setKey("");
              }}
              className="w-9 h-9 flex items-center justify-center rounded-md border border-slate-700 hover:border-slate-500 transition-colors focus:outline-none focus:ring-2 focus:ring-amber-400"
              aria-label="Lock inbox"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 py-10">
        <p className="text-xs text-slate-500 mb-6 inline-flex items-center gap-1.5">
          <RefreshCw className="w-3.5 h-3.5" />
          Updates automatically every 5 seconds
          {lastFetch && ` · last checked ${lastFetch.toLocaleTimeString("en-GB")}`}
        </p>

        {quotes.length === 0 ? (
          <div data-testid="admin-empty-state" className="border border-dashed border-slate-700 rounded-md p-14 text-center">
            <Inbox className="w-10 h-10 text-slate-600 mx-auto" />
            <p className="mt-4 font-heading text-xl font-bold text-white">No enquiries yet</p>
            <p className="mt-2 text-sm text-slate-400">
              Submit the quote form on the site and watch it appear here.
            </p>
          </div>
        ) : (
          <ul data-testid="admin-quotes-list" className="space-y-4">
            {quotes.map((q) => {
              const isNew = Date.now() - new Date(q.created_at).getTime() < 120000;
              return (
                <li
                  key={q.id}
                  data-testid={`admin-quote-${q.id}`}
                  className="bg-slate-900 border border-slate-800 rounded-md p-6"
                >
                  <div className="flex flex-wrap items-center gap-3">
                    <p className="font-heading text-lg font-bold text-white">{q.name}</p>
                    {isNew && (
                      <span className="text-[10px] font-bold uppercase tracking-[0.15em] bg-amber-400 text-slate-950 rounded-sm px-2 py-1">
                        New
                      </span>
                    )}
                    <span className="ml-auto text-xs text-slate-500">
                      {new Date(q.created_at).toLocaleString("en-GB", {
                        day: "numeric",
                        month: "short",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-slate-300">{q.message}</p>
                  <div className="mt-4 flex flex-wrap gap-3">
                    <a
                      data-testid={`admin-quote-phone-${q.id}`}
                      href={`tel:${q.phone.replace(/\s+/g, "")}`}
                      className="inline-flex items-center gap-2 text-xs font-bold bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-md px-3.5 py-2.5 transition-colors focus:outline-none focus:ring-2 focus:ring-amber-400"
                    >
                      <Phone className="w-3.5 h-3.5 text-amber-400" /> {q.phone}
                    </a>
                    <a
                      data-testid={`admin-quote-email-${q.id}`}
                      href={`mailto:${q.email}`}
                      className="inline-flex items-center gap-2 text-xs font-bold bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-md px-3.5 py-2.5 transition-colors focus:outline-none focus:ring-2 focus:ring-amber-400"
                    >
                      <Mail className="w-3.5 h-3.5 text-amber-400" /> {q.email}
                    </a>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </main>
    </div>
  );
};

export default Admin;
